import { useMemo } from 'react';
import { Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AppShell, type AppModule, type ModuleKey, type Role } from '@radiology/ui';
import { MODULE_DEFINITIONS, landingRoute, navigationFor } from './navigation';
import { accessibleModules, activeRoleIn, rolesIn, useSession } from '../auth/SessionContext';
import { ROLE_LABELS, normalizeRole } from './roles';
import { NotFoundPage } from './StatusPages';

const VALID_MODULES = new Set(MODULE_DEFINITIONS.map(m => m.key));

function isModuleKey(value: string | undefined): value is ModuleKey {
  return Boolean(value) && VALID_MODULES.has(value as ModuleKey);
}

/**
 * Wraps every in-module route and is the access guard for the portal.
 *
 * The role comes from the URL (/:module/:role/:section), not from a stored
 * preference. That matters for two reasons:
 *
 *  1. Deep links work. Opening /certifications/manager/dashboard shows the
 *     manager's screens, not whichever role happens to rank highest.
 *  2. It gives a place to check authorisation. A user who does not hold the
 *     role named in the URL is redirected out, so a route cannot be reached by
 *     typing it.
 *
 * This is a client-side guard and therefore a UX affordance, not a security
 * boundary — the API enforces authorisation independently (Phase 3).
 */
export function ModuleLayout() {
  const { module: moduleParam, role: roleParam } = useParams<{ module: string; role: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const session = useSession();

  const available = accessibleModules(session);

  const modules: AppModule[] = useMemo(
    () =>
      MODULE_DEFINITIONS.map(def => {
        const role = activeRoleIn(session, def.key);
        return {
          key: def.key,
          label: def.label,
          description: def.description,
          icon: def.icon,
          available: available.includes(def.key),
          to: landingRoute(def.key, role) ?? `/${def.key}`,
        };
      }),
    [session, available],
  );

  // Unknown module in the URL is a 404, not a bounce to the default module.
  if (!isModuleKey(moduleParam)) return <NotFoundPage />;

  const heldRoles = rolesIn(session, moduleParam);
  if (heldRoles.length === 0) return <Navigate to={`/no-access/${moduleParam}`} replace />;

  // The URL role must be a role the user actually holds in this module.
  const urlRole: Role | null = roleParam ? normalizeRole(roleParam) : null;
  if (!urlRole || !heldRoles.includes(urlRole)) {
    const fallback = landingRoute(moduleParam, activeRoleIn(session, moduleParam));
    return <Navigate to={fallback ?? `/no-access/${moduleParam}`} replace />;
  }

  const navGroups = navigationFor(moduleParam, urlRole);
  // A held role with no screens means the nav map and the role assignment
  // disagree. Send the user somewhere they can act rather than showing an
  // empty sidebar.
  if (navGroups.length === 0) return <Navigate to={`/no-access/${moduleParam}`} replace />;

  const currentItem = navGroups
    .flatMap(group => group.items)
    .find(item => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`));

  const moduleLabel = MODULE_DEFINITIONS.find(m => m.key === moduleParam)?.label ?? moduleParam;

  const handleChangeRole = (role: Role) => {
    session.setActiveRole(moduleParam, role);
    const target = landingRoute(moduleParam, role);
    // navigate() returns a promise in react-router v7; nothing depends on it.
    if (target) void navigate(target, { replace: true });
  };

  return (
    <AppShell
      modules={modules}
      activeModule={moduleParam}
      navGroups={navGroups}
      currentPath={location.pathname}
      user={session.user}
      userContext={{ ...session.context, role: urlRole }}
      availableRoles={heldRoles}
      pageTitle={currentItem?.label}
      breadcrumbs={[
        { label: moduleLabel },
        { label: ROLE_LABELS[urlRole] },
        ...(currentItem ? [{ label: currentItem.label }] : []),
      ]}
      onNavigate={to => void navigate(to)}
      onSignOut={session.signOut}
      onChangeRole={handleChangeRole}
    >
      <Outlet />
    </AppShell>
  );
}
